import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isOnboardingResponseForProject,
  isProgressionDataLoading,
  progressionQueryOptions
} from '../../utils/progressionQueryUtils';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { getSalesDailyTasks, getSalesOnboardingMap } from '../../services/api/implementation/impl';
import SalesDominoDealDocumentModal from '../../components/SalesDominoDealDocumentModal';
import SalesDominoTaskFlow from '../../components/SalesDominoTaskFlow';
import {
  getSalesDominoModalKind,
  getSalesPriorityNavigation,
  getSalesProgressionSubStepActionLabel,
  normalizeSalesDailyTaskList,
  resolveSalesDominoTaskFromList
} from '../../utils/salesDailyTaskTypes';
import { ProgressionTimelineBody } from './ProgressionTimelineSteps';

const mapStepStatus = (step, currentStepId) => {
  if (step.status === 'completed') return 'completed';
  if (
    step.id === currentStepId ||
    step.status === 'current' ||
    step.status === 'available' ||
    step.status === 'delayed'
  ) {
    return 'active';
  }
  return 'locked';
};

const mapSubStepStatus = (status) => {
  if (status === 'completed') return 'completed';
  if (status === 'current') return 'active';
  if (status === 'available' || status === 'pending') return 'available';
  return 'locked';
};

const mapSubStepUnit = (subStep, parentStep) => {
  if (subStep.unit) return subStep.unit;
  const unitId = subStep.unitId ?? subStep.unit_id ?? null;
  if (unitId == null) return null;
  return {
    id: unitId,
    unitName: subStep.unitName ?? subStep.unit_name ?? null,
    project_id: subStep.projectId ?? parentStep.projectId ?? null
  };
};

const mapApiSubSteps = (subSteps = [], parentStep = {}) =>
  subSteps.map((subStep) => {
    const mapped = {
      id: subStep.id,
      title: subStep.title,
      vertical: subStep.vertical || null,
      status: mapSubStepStatus(subStep.status),
      owner: subStep.owner?.name?.trim() || null,
      type: subStep.type || parentStep.type || null,
      taskId: subStep.taskId ?? parentStep.taskId ?? null,
      unit: mapSubStepUnit(subStep, parentStep)
    };

    return {
      ...mapped,
      actionLabel: getSalesProgressionSubStepActionLabel(mapped)
    };
  });

const mapApiStepsToStages = (steps = [], currentStepId) => {
  const stepTitleById = Object.fromEntries(steps.map((step) => [step.id, step.title]));

  return steps.map((step) => {
    const uiStatus = mapStepStatus(step, currentStepId);
    const subSteps = mapApiSubSteps(step.subSteps, step);
    const needsLabel =
      uiStatus === 'locked'
        ? step.needsLabel ||
          (step.dependsOn?.length
            ? `Needs: ${step.dependsOn.map((id) => stepTitleById[id]).filter(Boolean).join(', ')}`
            : null)
        : null;

    return {
      id: step.id,
      title: step.title,
      description: step.description || null,
      needsLabel,
      status: uiStatus,
      phase: step.status,
      owner:
        (typeof step.owner === 'object' ? step.owner?.name?.trim() : step.owner) || null,
      pendingDays: step.pendingDays,
      completedSubSteps: step.completedSubSteps ?? null,
      totalSubSteps: step.totalSubSteps ?? null,
      tag: step.status === 'delayed' ? 'Delayed' : step.tag,
      taskId: step.taskId,
      type: step.type,
      unit: step.unit ?? null,
      subSteps,
      actions:
        uiStatus === 'active' && step.actionLabel && subSteps.length === 0
          ? [step.actionLabel]
          : []
    };
  });
};

const SalesProgressionView = ({ projectID = null, salesManagerId = null }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeDominoTask, setActiveDominoTask] = useState(null);
  const [dominoModal, setDominoModal] = useState(null);

  const isSalesExecutive =
    Cookies.get('userVertical') === 'Sales' && Cookies.get('userDesignationLevel') === 'Low';

  const isFilterReady = projectID !== null && projectID !== undefined;
  const hasProjectSelected = isFilterReady && String(projectID) !== '';
  const resolvedProjectId = hasProjectSelected ? Number(projectID) : null;
  const hasSalesManagerSelected =
    salesManagerId !== null &&
    salesManagerId !== undefined &&
    String(salesManagerId) !== '';
  const resolvedSalesManagerId = hasSalesManagerSelected ? Number(salesManagerId) : null;

  const {
    data: onboardingResponse,
    isPending,
    isFetching,
    isError
  } = useQuery({
    queryKey: ['salesOnboardingMap', resolvedProjectId, resolvedSalesManagerId],
    queryFn: () => getSalesOnboardingMap(resolvedProjectId, resolvedSalesManagerId),
    enabled: hasProjectSelected && hasSalesManagerSelected,
    ...progressionQueryOptions
  });

  const { data: salesDailyTasksResponse } = useQuery({
    queryKey: ['salesDailyTasks', resolvedProjectId],
    queryFn: () => getSalesDailyTasks(resolvedProjectId),
    enabled: hasProjectSelected,
    ...progressionQueryOptions
  });

  const isInitialLoad = isProgressionDataLoading({
    isPending,
    isFetching,
    response: onboardingResponse,
    projectId: resolvedProjectId
  });

  const salesDailyTasks = normalizeSalesDailyTaskList(salesDailyTasksResponse);

  const stages = useMemo(() => {
    if (!isOnboardingResponseForProject(onboardingResponse, resolvedProjectId)) {
      return [];
    }

    const mapped = mapApiStepsToStages(
      onboardingResponse?.data?.steps,
      onboardingResponse?.data?.currentStepId
    );

    if (isSalesExecutive) return mapped;

    return mapped.map((stage) => ({
      ...stage,
      subSteps: stage.subSteps?.map((subStep) =>
        getSalesDominoModalKind(subStep) === 'leadPricing'
          ? { ...subStep, actionLabel: null }
          : subStep
      )
    }));
  }, [onboardingResponse, resolvedProjectId, isSalesExecutive]);

  const progressPercent = isOnboardingResponseForProject(onboardingResponse, resolvedProjectId)
    ? onboardingResponse?.data?.progress?.percent ?? 0
    : 0;

  const refreshProgression = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['salesOnboardingMap', resolvedProjectId, resolvedSalesManagerId]
    });
    await queryClient.invalidateQueries({
      queryKey: ['salesDailyTasks', resolvedProjectId]
    });
  }, [queryClient, resolvedProjectId, resolvedSalesManagerId]);

  const closeDominoModal = useCallback(() => {
    setActiveDominoTask(null);
    setDominoModal(null);
  }, []);

  const handleDominoComplete = useCallback(async () => {
    closeDominoModal();
    await refreshProgression();
  }, [closeDominoModal, refreshProgression]);

  const handleSalesProgressionAction = useCallback(
    (item) => {
      const modalKind = getSalesDominoModalKind(item);

      if (modalKind) {
        const task = resolveSalesDominoTaskFromList(salesDailyTasks, {
          taskId: item.taskId,
          type: item.type,
          unit: item.unit,
          projectId: resolvedProjectId
        });

        if (task) {
          setActiveDominoTask(task);
          setDominoModal(modalKind);
          return;
        }

        const navigation = getSalesPriorityNavigation(item.type);
        if (navigation) {
          navigate(navigation.path, {
            state: {
              secondaryTab: navigation.secondaryTab,
              projectID: resolvedProjectId,
              openBulkUnitUpdate: navigation.openBulkUnitUpdate ?? false
            }
          });
          return;
        }

        toast.error('Unable to load task details. Please try again.');
        return;
      }

      const navigation = getSalesPriorityNavigation(item.type);
      if (!navigation) return;

      navigate(navigation.path, {
        state: {
          secondaryTab: navigation.secondaryTab,
          projectID: resolvedProjectId,
          openBulkUnitUpdate: navigation.openBulkUnitUpdate ?? false
        }
      });
    },
    [navigate, resolvedProjectId, salesDailyTasks]
  );

  const handleStageAction = useCallback(
    (stage) => {
      handleSalesProgressionAction(stage);
    },
    [handleSalesProgressionAction]
  );

  const handleSubStepAction = useCallback(
    (subStep) => {
      if (getSalesDominoModalKind(subStep) === 'leadPricing' && !isSalesExecutive) {
        return;
      }
      handleSalesProgressionAction(subStep);
    },
    [handleSalesProgressionAction, isSalesExecutive]
  );

  return (
    <>
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-xl font-semibold text-[#1E4A53]">Sales Mission Progression</h3>
            {hasProjectSelected && (
              <p className="mt-0.5 text-xs text-gray-500">Project-specific sales onboarding</p>
            )}
          </div>
          {!isInitialLoad && !isError && hasProjectSelected && hasSalesManagerSelected && (
            <div className="shrink-0 text-right">
              <p className="text-2xl font-bold leading-none text-[#0f2a45]">{progressPercent}%</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Complete
              </p>
            </div>
          )}
        </div>

        {!hasProjectSelected && (
          <div className="py-12 text-center text-sm text-gray-500">
            Select a project to view sales onboarding progress.
          </div>
        )}

        {hasProjectSelected && !hasSalesManagerSelected && (
          <div className="py-12 text-center text-sm text-gray-500">
            Select a sales manager to view onboarding progress.
          </div>
        )}

        {hasProjectSelected && hasSalesManagerSelected && isInitialLoad && (
          <div className="py-12 text-center text-sm text-gray-500">
            Loading sales progression...
          </div>
        )}

        {hasProjectSelected && hasSalesManagerSelected && isError && (
          <div className="py-12 text-center text-sm text-rose-600">
            Unable to load sales progression. Please try again.
          </div>
        )}

        {hasProjectSelected &&
          hasSalesManagerSelected &&
          !isInitialLoad &&
          !isError &&
          stages.length === 0 && (
          <div className="py-12 text-center text-sm text-gray-500">
            No sales onboarding steps available for this project yet.
          </div>
        )}

        {hasProjectSelected &&
          hasSalesManagerSelected &&
          !isInitialLoad &&
          !isError &&
          stages.length > 0 && (
          <ProgressionTimelineBody
            stages={stages}
            onAction={handleStageAction}
            onSubStepAction={handleSubStepAction}
          />
        )}
      </div>

      {dominoModal === 'leadPricing' && activeDominoTask && (
        <SalesDominoTaskFlow
          task={activeDominoTask}
          onClose={closeDominoModal}
          onComplete={handleDominoComplete}
        />
      )}

      {dominoModal === 'dealDocument' && activeDominoTask && (
        <SalesDominoDealDocumentModal
          task={activeDominoTask}
          onClose={closeDominoModal}
          onComplete={handleDominoComplete}
        />
      )}
    </>
  );
};

export default SalesProgressionView;
